import {
  AutoFilter,
  DataListView,
  DataTable,
  ErrorMessage,
  Filter,
} from "@/shared/components";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { LocalStorage } from "@/shared/utils/localstorage";
import type { DocumentNode, MutationFunctionOptions } from "@apollo/client";
import { useQuery } from "@apollo/client";
import React, { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import type { Schema } from "../Form/types";
import type { CrudTotalSchemaItem } from "./CrudTotal";
import { CrudTotal } from "./CrudTotal";
import { QuickFilter } from "./QuickFilter";
import type {
  ColumnDefinition,
  CreateSchemaDefinition,
  CrudTexts,
  MutationVariables,
  TableData,
} from "./types";

export interface CrudListProps<
  Row extends { id: string } = { id: string },
  Filter = Record<string, unknown>,
> {
  listMetrics?: React.ComponentType<{ filter: Filter }>;
  totalSchema?: CrudTotalSchemaItem[];
  filterSchema?: Schema;
  filterQuery?: DocumentNode; // GraphQL query
  filterQueryVariables?: MutationVariables;
  autoFilter?: boolean;
  listQuery: DocumentNode; // GraphQL query
  listPollInterval?: number;
  listQueryVariables?: MutationVariables;
  texts?: CrudTexts;
  getTableData: (data: unknown) => TableData<Row>;
  mapColumns: (row: Row) => any[];
  columns: ColumnDefinition<Row>[];
  download?: boolean;
  downloadReportType?: string;
  /**
   * Нормализация значений фильтра перед отправкой в listQuery (регистр,
   * пробелы и т.п.). На стейт формы и URL не влияет.
   */
  normalizeFilter?: (filter: Record<string, any>) => Record<string, any>;
  createSchema?: CreateSchemaDefinition;
  deleteMutation?: DocumentNode; // GraphQL mutation
  deleteMutationVariables?: MutationVariables;
  restoreMutation?: DocumentNode; // GraphQL mutation
  repeatMutation?: DocumentNode; // GraphQL mutation
  repeatMutationVariables?: MutationVariables;
  repeatFromHlsMutation?: DocumentNode; // GraphQL mutation
  downloadButton?: React.ReactNode;
  /** Сохранять значения фильтра в localStorage и восстанавливать при возврате */
  persistFilters?: boolean;
  /**
   * У страницы есть представление карточками. Само переключение — по ширине
   * экрана: ниже `md` показываем карточки, выше остаётся таблица.
   */
  useMobileView?: boolean;
  mobileColumns?: DataListViewColumns<Row>;
  /** Какие строки можно выбирать; у остальных чекбокс скрыт */
  hasShowSelect?: (row: Row) => boolean;
  /** Показывать чекбоксы, даже если нет мутаций удаления/повтора */
  forceSelectableRows?: boolean;
  /** Кнопки в тулбаре списка; функция получает id выбранных строк */
  headButtons?: React.ReactNode | ((selectedIds: string[]) => React.ReactNode);
  /** Своя кнопка создания вместо стандартной */
  createButton?: React.ReactNode;
  /** Стандартную кнопку создания рисовать не нужно */
  enableCreateButton?: boolean;
  /** Полностью заменяет набор кнопок над выбранными строками */
  selectableComponent?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
  /** Своя кнопка удаления вместо стандартной (только для карточек) */
  customDeleteButton?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
}

function getIds<Row extends { id: string }>(
  nodes: Row[],
  selectedRows: number[]
): string[] {
  return nodes
    .filter((_, index) => selectedRows.includes(index))
    .map(({ id }) => id);
}

const ls = LocalStorage("admin-crud");

/**
 * Стабильная сериализация фильтра: ключи сортируются, поэтому два объекта с
 * одинаковым содержимым дают одну строку независимо от порядка вставки.
 */
const stableFilterKey = (filter: Record<string, unknown>): string =>
  JSON.stringify(
    Object.keys(filter)
      .sort()
      .map((key) => [key, filter[key]])
  );

const parseFilter = (filter: string | null): Record<string, any> => {
  if (!filter) return {};
  try {
    const parsed = JSON.parse(filter);
    // Фильтруем строки "null" и пустые значения
    if (typeof parsed === "object" && parsed !== null) {
      return Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => {
          if (value === "null" || value === null || value === undefined)
            return false;
          if (Array.isArray(value) && value.length === 0) return false;
          if (value === "") return false;
          return true;
        })
      );
    }
    return parsed;
  } catch (err) {
    console.error("Failed to parse filter:", err);
    return {};
  }
};

export function CrudList<
  Row extends { id: string } = { id: string },
  Filter = Record<string, unknown>,
>({
  listMetrics,
  totalSchema,
  filterSchema,
  filterQuery,
  filterQueryVariables,
  autoFilter,
  listQuery,
  listPollInterval,
  listQueryVariables,
  texts = {},
  getTableData,
  mapColumns,
  columns,
  download,
  downloadReportType,
  normalizeFilter,
  createSchema,
  deleteMutation,
  deleteMutationVariables,
  restoreMutation,
  repeatMutation,
  repeatMutationVariables,
  repeatFromHlsMutation,
  downloadButton,
  persistFilters,
  useMobileView,
  mobileColumns,
  hasShowSelect,
  forceSelectableRows,
  headButtons,
  createButton: customCreateButton,
  enableCreateButton,
  selectableComponent,
  customDeleteButton,
}: CrudListProps<Row, Filter>) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const showMobileView = !!useMobileView && !!mobileColumns && isMobile;
  const filterSchemas = useMemo(() => {
    const result: {
      filter: Schema;
      quickFilter: Schema;
    } = {
      filter: {},
      quickFilter: {},
    };

    if (filterSchema) {
      Object.entries(filterSchema).forEach(([key, field]) => {
        if (field?.priority === "quick") {
          result.quickFilter[key] = field;
        } else {
          result.filter[key] = field;
        }
      });
    }

    return result;
  }, [filterSchema]);

  const filtersStorageKey = `filters:${location.pathname}`;

  // Восстанавливаем сохранённый фильтр — но только если в URL его ещё нет:
  // ссылка с фильтром всегда важнее сохранённого состояния
  useEffect(() => {
    if (!persistFilters || searchParams.get("filter")) return;

    // В хранилище лежит та же строка, что уходит в URL-параметр `filter`
    const savedFilter = ls.getItem<string>(filtersStorageKey);
    if (!savedFilter) return;

    try {
      const parsedFilter = JSON.parse(savedFilter);
      if (typeof parsedFilter !== "object" || parsedFilter === null) return;

      const cleanFilter = Object.fromEntries(
        Object.entries(parsedFilter).filter(([, value]) => {
          if (value === null || value === undefined || value === "")
            return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        })
      );

      if (Object.keys(cleanFilter).length === 0) return;

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("filter", JSON.stringify(cleanFilter));
      setSearchParams(nextParams, { replace: true });
    } catch (err) {
      console.error("Failed to restore filters from localStorage:", err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const queryFilter = parseFilter(searchParams.get("filter"));
  // filter — как есть, для стейта и URL
  const filter = Object.assign({}, queryFilter, listQueryVariables?.filter);

  /**
   * initialValues для формы фильтра: только НЕ quick-поля, пересчитываются при
   * изменении фильтра — чтобы форма догоняла URL при переходе по истории.
   *
   * Ключом мемоизации служит сериализованный фильтр, а не сам объект: `filter`
   * пересобирается на каждом рендере, и от его идентичности форма уходила бы в
   * бесконечный цикл переинициализации.
   */
  const filterKey = stableFilterKey(filter);
  const filterInitialValues = useMemo(() => {
    const nonQuickKeys = Object.keys(filterSchemas.filter);

    return Object.fromEntries(
      Object.entries(filter).filter(([key]) => nonQuickKeys.includes(key))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, filterSchemas.filter]);

  // Только для запроса: преобразуем строки 'true'/'false' в boolean
  const typedFilter = Object.fromEntries(
    Object.entries(filter).map(([key, value]: [string, unknown]) => {
      if (value === "true") return [key, true];
      if (value === "false") return [key, false];
      return [key, value];
    })
  );

  const gqlFilter = normalizeFilter
    ? normalizeFilter(typedFilter)
    : typedFilter;

  const page = parseInt(searchParams.get("page") || "0", 10);
  const rowsPerPage = parseInt(
    searchParams.get("rowsPerPage") ||
      String(ls.getItemOrDefault<number>("rowsPerPage", 10)),
    10
  );

  const variables = {
    ...listQueryVariables,
    filter: Object.keys(gqlFilter).length > 0 ? gqlFilter : undefined,
    page,
    rowsPerPage,
  };

  const refetchQueries = [{ query: listQuery, variables }];

  const listQueryResult = useQuery(listQuery, {
    fetchPolicy: "cache-and-network",
    pollInterval: listPollInterval,
    variables,
  });

  /**
   * DataListView сам решает, когда звать удаление/восстановление, и ждёт
   * функцию в стиле useMutation. Кнопки DataTable, наоборот, получают готовые
   * обработчики — отсюда две разные обёртки над одними мутациями.
   */
  const createMutationRunner = (
    mutationDoc?: DocumentNode,
    baseVariables?: MutationVariables | (() => MutationVariables)
  ) => {
    if (!mutationDoc) return undefined;

    return (options?: MutationFunctionOptions<any, { ids: string[] }>) => {
      const {
        variables: optionVariables,
        refetchQueries: optionRefetch,
        ...restOptions
      } = options || {};

      const resolvedBaseVariables =
        typeof baseVariables === "function"
          ? baseVariables()
          : (baseVariables ?? {});

      listQueryResult.client.mutate({
        mutation: mutationDoc,
        refetchQueries: optionRefetch ?? refetchQueries,
        variables: {
          ...resolvedBaseVariables,
          ...optionVariables,
        } as { ids: string[] },
        ...restOptions,
      });
    };
  };

  const deleteItemsRunner = createMutationRunner(
    deleteMutation,
    deleteMutationVariables
  );
  const restoreItemsRunner = createMutationRunner(restoreMutation);

  const navigate = (params: Record<string, any>) => {
    const searchParamsObj: Record<string, any> = {
      filter,
      page,
      rowsPerPage,
      ...params,
    };

    // Сохраняем параметры диалогов (tab и id) при изменении фильтра
    const currentTab = searchParams.get("tab");
    const currentId = searchParams.get("id");
    if (currentTab) {
      searchParamsObj.tab = currentTab;
    }
    if (currentId) {
      searchParamsObj.id = currentId;
    }

    // Фильтруем null значения и пустые массивы перед сериализацией
    const cleanFilter = Object.fromEntries(
      Object.entries(searchParamsObj.filter).filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (value === "") return false;
        return true;
      })
    );

    searchParamsObj.filter = JSON.stringify(cleanFilter, (_key, value) => {
      if (moment.isMoment(value)) {
        return value.format();
      }
      return value;
    });

    if (persistFilters) {
      if (Object.keys(cleanFilter).length > 0) {
        ls.setItem(filtersStorageKey, searchParamsObj.filter);
      } else {
        ls.removeItem(filtersStorageKey);
      }
    }

    const urlSearchParams = new URLSearchParams();
    Object.entries(searchParamsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        urlSearchParams.set(key, String(value));
      }
    });

    // Не засоряем историю, если ничего не изменилось. Сравниваем по
    // отсортированным параметрам — порядок ключей в URL значения не имеет
    const currentParams = new URLSearchParams(location.search);
    currentParams.sort();
    const nextParams = new URLSearchParams(urlSearchParams);
    nextParams.sort();
    if (nextParams.toString() === currentParams.toString()) return;

    routerNavigate(`${location.pathname}?${urlSearchParams.toString()}`);
  };

  const pickQuickFilterValues = (
    source: Record<string, unknown>
  ): Record<string, unknown> => {
    const quickKeys = Object.keys(filterSchemas.quickFilter);

    return Object.fromEntries(
      Object.entries(source).filter(([key]) => quickKeys.includes(key))
    );
  };

  const onFilterSubmit = (formFilter: Record<string, unknown>) => {
    const mergedFilter = {
      ...pickQuickFilterValues(filter),
      ...formFilter,
    };
    navigate({ filter: mergedFilter, page: 0 });
  };

  // Debounce живёт внутри полей AutoFilter — второй слой здесь не нужен
  const onAutoFilterSubmit = (formFilter: Record<string, unknown>) => {
    const currentFormFilter = Object.fromEntries(
      Object.entries(filter).filter(([key]) =>
        Object.keys(filterSchemas.filter).includes(key)
      )
    );

    // AutoFilter дёргает onChange в том числе при монтировании
    if (stableFilterKey(formFilter) === stableFilterKey(currentFormFilter)) {
      return;
    }

    const mergedFilter = {
      ...pickQuickFilterValues(filter),
      ...formFilter,
    };

    navigate({ filter: mergedFilter, page: 0 });
  };

  const onChangePage = (page: number) => {
    navigate({ page });
  };

  const onChangeRowsPerPage = (rowsPerPage: number) => {
    navigate({ rowsPerPage, page: 0 });
    ls.setItem("rowsPerPage", rowsPerPage);
  };

  const onQuickFilterChange = (filter: Record<string, unknown>) => {
    navigate({ filter, page: 0 });
  };

  const { nodes = [], count = 0 } =
    getTableData(listQueryResult.data || listQueryResult.previousData) || {};
  const tableData = nodes.map(mapColumns);

  const selectableRows =
    forceSelectableRows || !!deleteMutation || !!repeatMutation;

  const shouldShowCreateButton = enableCreateButton ?? !!createSchema;

  const defaultCreateButton = shouldShowCreateButton
    ? {
        text: texts.createItem,
        component: Link,
        to: "?tab=create",
      }
    : undefined;

  const createButton = customCreateButton || defaultCreateButton;

  const deleteButton = deleteMutation && {
    text: texts.deleteItems,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);

      const onClickRestore = () => {
        if (!restoreMutation) return;

        listQueryResult.client
          .mutate({
            mutation: restoreMutation,
            refetchQueries,
            variables: {
              ids,
            },
          })
          .then(() => {
            return enqueueSnackbar(texts.itemsRestored, {
              variant: "success",
            });
          })
          .catch((error) => {
            enqueueSnackbar(error.message, {
              variant: "error",
            });
          });
      };

      if (!deleteMutation) return;

      listQueryResult.client
        .mutate({
          mutation: deleteMutation,
          refetchQueries,
          variables: {
            ...deleteMutationVariables,
            ids,
          },
        })
        .then(() => {
          if (restoreMutation) {
            enqueueSnackbar(texts.itemsDeleted, {
              variant: "success",
              action: (
                <Button size="small" color="inherit" onClick={onClickRestore}>
                  {texts.cancel}
                </Button>
              ),
            });
          } else {
            enqueueSnackbar(texts.itemsDeleted, {
              variant: "success",
            });
          }
          return;
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const restoreButton = restoreMutation && {
    text: texts.restoreItems,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);

      if (!restoreMutation) return;

      listQueryResult.client
        .mutate({
          mutation: restoreMutation,
          refetchQueries,
          variables: {
            ids,
          },
        })
        .then(() => {
          return enqueueSnackbar(texts.itemsRestored, {
            variant: "success",
          });
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const repeatButton = repeatMutation && {
    text: texts.repeatItems,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);

      if (!repeatMutation) return;

      listQueryResult.client
        .mutate({
          mutation: repeatMutation,
          refetchQueries,
          variables: {
            ...repeatMutationVariables,
            ids,
          },
        })
        .then(() => {
          return enqueueSnackbar(texts.itemsRepeated, {
            variant: "success",
          });
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const repeatFromHlsButton = repeatFromHlsMutation && {
    text: texts.repeatItemsFromHls,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);

      if (!repeatFromHlsMutation) return;

      listQueryResult.client
        .mutate({
          mutation: repeatFromHlsMutation,
          refetchQueries,
          variables: {
            ...repeatMutationVariables,
            ids,
          },
        })
        .then(() => {
          return enqueueSnackbar(texts.itemsRepeated, {
            variant: "success",
          });
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const refetchButton = {
    text: texts.refetchList,
    onClick: () => listQueryResult.refetch(),
  };

  return (
    <Box>
      {listQueryResult.error && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid size={12}>
            <ErrorMessage error={listQueryResult.error} />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={1}>
        {filterSchemas.filter ? (
          <Grid size={12}>
            <Card>
              <CardContent>
                {autoFilter ? (
                  <AutoFilter
                    onSubmit={onAutoFilterSubmit}
                    initialValues={filterInitialValues}
                    schema={filterSchemas.filter}
                    title={texts.listTitle}
                  />
                ) : (
                  <Filter
                    query={filterQuery}
                    variables={filterQueryVariables}
                    onSubmit={onFilterSubmit}
                    initialValues={filterInitialValues}
                    schema={filterSchemas.filter}
                    title={texts.listTitle}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid size={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="h5">{texts.listTitle}</Typography>
            </Stack>
          </Grid>
        )}

        {totalSchema && listQueryResult.data && (
          <Grid size={12}>
            <CrudTotal schema={totalSchema} data={listQueryResult?.data} />
          </Grid>
        )}

        {listMetrics && (
          <Grid size={12}>
            {React.createElement(listMetrics, {
              filter: variables.filter as Filter,
            })}
          </Grid>
        )}

        <Grid size={12}>
          <Card>
            {showMobileView && mobileColumns ? (
              <>
                {Object.keys(filterSchemas.quickFilter).length > 0 && (
                  <CardContent>
                    <QuickFilter
                      quickFilter={filterSchemas.quickFilter}
                      currentFilter={filter}
                      onFilterChange={onQuickFilterChange}
                    />
                  </CardContent>
                )}
                <Box sx={{ px: 2 }}>
                  <DataListView<Row>
                    data={{ nodes, count }}
                    columns={mobileColumns}
                    deleteItems={deleteItemsRunner}
                    restoreItems={restoreItemsRunner}
                    refetch={() => listQueryResult.refetch()}
                    createButton={customCreateButton}
                    createLink={
                      !customCreateButton && defaultCreateButton
                        ? defaultCreateButton.to
                        : undefined
                    }
                    headButtons={headButtons}
                    deleteButton={customDeleteButton}
                    selectableComponent={selectableComponent}
                    hasShowSelect={hasShowSelect}
                    selectableRows={selectableRows}
                    isLoading={listQueryResult.loading}
                    error={listQueryResult.error}
                    texts={{
                      createItem: texts.createItem ?? "Создать",
                      deleteItems: texts.deleteItems ?? "Удалить",
                      deleteItemsSuccess:
                        texts.itemsDeleted ?? "Успешно удалено",
                      restoreItems: texts.restoreItems ?? "Восстановить",
                      restoreItemsSuccess:
                        texts.itemsRestored ?? "Успешно восстановлено",
                    }}
                  />
                </Box>
              </>
            ) : (
              <DataTable
                loading={listQueryResult.loading}
                download={download}
                downloadReportType={downloadReportType}
                title="Список"
                data={tableData}
                nodes={nodes}
                columns={columns}
                selectableRows={selectableRows}
                hasShowSelect={hasShowSelect}
                headButtons={headButtons}
                selectableComponent={selectableComponent}
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                serverSide
                onChangePage={onChangePage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                createButton={createButton}
                deleteButton={deleteButton}
                restoreButton={restoreButton}
                repeatButton={repeatButton}
                refetchButton={refetchButton}
                repeatFromHlsButton={repeatFromHlsButton}
                downloadButton={downloadButton}
                textLabels={{ selected: "выбрано" }}
                quickFilter={filterSchemas.quickFilter}
                currentFilter={filter}
                onFilterChange={onQuickFilterChange}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
